/**
 * Data Prefetcher - Proactively caches critical health data for offline use
 * Run on app startup to ensure data is available even when offline
 */

import { supabase } from '@/integrations/supabase/client';
import { offlineDataStore, CacheableTable } from '@/lib/offlineDataStore';
import { logger } from '@/lib/logger';

interface PrefetchConfig {
  table: CacheableTable;
  select: string;
  filter?: (query: ReturnType<typeof supabase.from>) => ReturnType<typeof supabase.from>;
  queryKey?: string;
  priority: 'high' | 'medium' | 'low';
}

const PREFETCH_CONFIGS: PrefetchConfig[] = [
  // High priority - needed for main medication page
  {
    table: 'prescriptions',
    select: '*',
    filter: (q) => q.eq('is_active', true).order('medication_name'),
    priority: 'high',
  },
  {
    table: 'medications',
    select: '*',
    filter: (q) => q.eq('logged_date', new Date().toISOString().split('T')[0]),
    queryKey: `date:${new Date().toISOString().split('T')[0]}`,
    priority: 'high',
  },
  // Medium priority - needed for dashboard
  {
    table: 'appointments',
    select: '*',
    filter: (q) => q.gte('appointment_date', new Date().toISOString().split('T')[0]).order('appointment_date').limit(10),
    queryKey: 'upcoming',
    priority: 'medium',
  },
  {
    table: 'symptoms',
    select: '*',
    filter: (q) => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return q.gte('logged_date', weekAgo.toISOString().split('T')[0]).order('logged_date', { ascending: false });
    },
    queryKey: 'days:7',
    priority: 'medium',
  },
  {
    table: 'daily_moods',
    select: '*',
    filter: (q) => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return q.gte('logged_date', weekAgo.toISOString().split('T')[0]).order('logged_date', { ascending: false });
    },
    queryKey: 'days:7',
    priority: 'medium',
  },
  {
    table: 'vital_signs',
    select: '*',
    filter: (q) => {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      return q.gte('logged_date', monthAgo.toISOString().split('T')[0]).order('logged_date', { ascending: false });
    },
    queryKey: 'days:30',
    priority: 'medium',
  },
  // Low priority - nice to have
  {
    table: 'care_team_members',
    select: '*',
    filter: (q) => q.order('provider_name'),
    priority: 'low',
  },
  {
    table: 'user_settings',
    select: '*',
    priority: 'low',
  },
  {
    table: 'side_effects',
    select: '*',
    filter: (q) => {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      return q.gte('occurred_at', monthAgo.toISOString());
    },
    queryKey: 'recent',
    priority: 'low',
  },
];

class DataPrefetcher {
  private isPrefetching = false;
  private prefetchPromise: Promise<void> | null = null;

  /**
   * Prefetch all critical data for offline use
   * Returns immediately if already prefetching
   */
  async prefetchAll(userId: string): Promise<void> {
    if (this.isPrefetching) {
      return this.prefetchPromise!;
    }

    if (!navigator.onLine) {
      logger.log('Skipping prefetch - offline');
      return;
    }

    this.isPrefetching = true;
    logger.log('Starting data prefetch for offline use...');

    this.prefetchPromise = this.executePrefetch(userId);

    try {
      await this.prefetchPromise;
    } finally {
      this.isPrefetching = false;
      this.prefetchPromise = null;
    }
  }

  private async executePrefetch(userId: string): Promise<void> {
    const startTime = Date.now();
    let successCount = 0;
    let errorCount = 0;

    // Group by priority
    const highPriority = PREFETCH_CONFIGS.filter(c => c.priority === 'high');
    const mediumPriority = PREFETCH_CONFIGS.filter(c => c.priority === 'medium');
    const lowPriority = PREFETCH_CONFIGS.filter(c => c.priority === 'low');

    // Detect low-memory devices (Android Go, old Android 10 phones, etc.)
    // navigator.deviceMemory returns RAM tier in GB (0.25, 0.5, 1, 2, 4, 8...)
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    const isLowMemoryDevice = typeof deviceMemory === 'number' && deviceMemory <= 2;
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
    const budgetMode = isLowMemoryDevice || saveData;

    if (budgetMode) {
      logger.log(`Prefetch: budget mode active (deviceMemory=${deviceMemory ?? 'unknown'}GB, saveData=${saveData})`);
    }

    // Fetch high priority first (in parallel) - always runs
    await Promise.all(
      highPriority.map(async (config) => {
        const result = await this.prefetchTable(config, userId);
        if (result) successCount++;
        else errorCount++;
      })
    );

    // On budget devices, skip medium+low entirely. The on-demand prefetcher
    // (useTabDataPrefetcher) will fetch them when the user actually navigates
    // to those screens. This avoids OOM kills on old Android 10 hardware.
    if (budgetMode) {
      logger.log(`Prefetch (budget): ${successCount} high-priority tables cached, ${errorCount} errors, ${Date.now() - startTime}ms`);
      return;
    }

    const runIdle = (cb: () => Promise<void>) => {
      if ('requestIdleCallback' in window) {
        (window as Window).requestIdleCallback(() => { void cb(); }, { timeout: 5000 });
      } else {
        setTimeout(() => { void cb(); }, 100);
      }
    };

    // Defer medium priority to idle time (was blocking the main thread before)
    runIdle(async () => {
      await Promise.all(
        mediumPriority.map(async (config) => {
          const result = await this.prefetchTable(config, userId);
          if (result) successCount++;
          else errorCount++;
        })
      );

      // Then low priority on a second idle pass
      runIdle(async () => {
        await Promise.all(
          lowPriority.map(async (config) => {
            const result = await this.prefetchTable(config, userId);
            if (result) successCount++;
            else errorCount++;
          })
        );
        logger.log(`Prefetch complete: ${successCount} tables cached, ${errorCount} errors, ${Date.now() - startTime}ms`);
      });
    });
  }

  private async prefetchTable(config: PrefetchConfig, userId: string): Promise<boolean> {
    try {
      let query = supabase.from(config.table).select(config.select).eq('user_id', userId);

      if (config.filter) {
        query = config.filter(query) as typeof query;
      }

      const { data, error } = await query;

      if (error) {
        logger.error(`Prefetch failed for ${config.table}:`, error);
        return false;
      }

      if (data) {
        await offlineDataStore.set(config.table, userId, data, config.queryKey);
        logger.log(`Prefetched ${config.table}: ${data.length} items`);
      }

      return true;
    } catch (error) {
      logger.error(`Prefetch error for ${config.table}:`, error);
      return false;
    }
  }

  /**
   * Prefetch data for a specific table on demand
   */
  async prefetchSingleTable(table: CacheableTable, userId: string): Promise<void> {
    const config = PREFETCH_CONFIGS.find(c => c.table === table);
    if (config) {
      await this.prefetchTable(config, userId);
    }
  }

  /**
   * Clear stale data and refresh caches
   */
  async refreshCaches(userId: string): Promise<void> {
    await offlineDataStore.clearStaleEntries();
    await this.prefetchAll(userId);
  }
}

export const dataPrefetcher = new DataPrefetcher();

/**
 * Initialize prefetching when user logs in
 */
export async function initializeOfflineData(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user && navigator.onLine) {
      // Start prefetching in the background
      dataPrefetcher.prefetchAll(user.id);
    }

    // Set up listener for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user && navigator.onLine) {
        // Prefetch on sign in
        dataPrefetcher.prefetchAll(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        // Clear cached data on sign out
        if (session?.user) {
          await offlineDataStore.clearAllForUser(session.user.id);
        }
      }
    });

    // Prefetch when coming back online
    window.addEventListener('online', async () => {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        dataPrefetcher.prefetchAll(currentUser.id);
      }
    });

  } catch (error) {
    logger.error('Failed to initialize offline data:', error);
  }
}
